/**
 * Songbook Data — TurfPulse Vol. 1
 * =================================
 * Single source of truth for all 5 songs in the TurfPulse Songbook.
 * Each song carries: specs, production map, chord chart, full lyrics,
 * music video treatment, rollout info, and an embed slot.
 *
 * Add a 6th song here and it auto-renders on /about/the-songbook.
 */

export const SONGBOOK = [
  // ──────────────────────────────────────────────────────────────────
  {
    id: 'he-makes-it-grow',
    number: 1,
    title: 'He Makes It Grow',
    subtitle: 'A Praise Reggae · Rooted in 1 Corinthians 3:7',
    genre: 'Praise Reggae',
    emoji: '🙏',
    accent: 'amber',  // tailwind tone family
    foundation: true,
    scripture: '"So neither the one who plants nor the one who waters is anything, but only God, who makes things grow." — 1 Corinthians 3:7',
    rollout: 'Sun 5/18 morning · church day',
    caption: 'Before any of this — the probes, the code, the lawn, the company — there\'s the One who makes it grow. 1 Corinthians 3:7. I planted. Apollos watered. But only God gives the increase. 🙏 Made a song about it. All glory to Him.',
    specs: [
      ['Tempo', '70 BPM (slower than One Drop — reverent, not party)'],
      ['Key', 'G major (warm, uplifting — the praise key)'],
      ['Time', '4/4, one-drop with gospel hand-claps on 2 & 4 in the chorus'],
      ['Vibe', 'Bob Marley "Forever Loving Jah" × Morgan Heritage × Black gospel choir × Aswad organ swells'],
      ['Length', '~4:30'],
    ],
    production: [
      ['Kit', 'One-drop — kick on 3, gospel hand-claps on 2 & 4 (chorus only), brushed ride bell on the bridge'],
      ['Bass', 'Walking bass — slow, melodic, supports the harmony'],
      ['Guitar', 'Skank on 2 & 4, clean tone, light tremolo'],
      ['Organ', 'HAMMOND B3 with Leslie swell — church organ meets reggae bubble. Mix it loud.'],
      ['Piano', 'Wurlitzer chords on offbeats, gospel piano flourishes on chorus'],
      ['Horns', 'Trumpet + sax + trombone — soft pads on verses, swells on chorus, solo trumpet on bridge'],
      ['Perc', 'Tambourine on 2 & 4 (church + reggae crossover)'],
      ['Vox', 'LEAD: heartfelt baritone (G3-D4). CHOIR: 4-part gospel choir on chorus. Harmonies: Morgan Heritage family-band stack on verses.'],
    ],
    arrangement: '[Intro :20] organ swell + spoken word "First Corinthians, chapter three…" → [Verse 1 :48] solo lead, sparse → [Pre :16] choir hums underneath → [Chorus :40] full choir, hand-claps, hands-up moment → [Verse 2 :48] band fuller, harmonies enter → [Pre :16] → [Chorus :40] → [Bridge :40] half-time, scripture sung directly, trumpet solo response → [Final Chorus :48] modulate up to A major, gospel choir double → [Outro :32] organ + choir tag "He makes it grow… He makes it grow…"',
    chords: {
      verse: '| Gmaj7 | Bm7 | Em9 | Cmaj7 |  | Gmaj7 | Bm7 | Am7 | D7sus  D7 |',
      preChorus: '| Cmaj7 | Bm7 | Am7 | D7 |',
      chorus: '| C | G | D | Em |  | C | G | Am7 | D7 |  | C | G | D | Em  Em/D |  | C | D | G | G  D7 |',
      bridge: '| Cmaj7 | Gmaj7 | D | Em |  (×4)',
      modulation: 'Final chorus: up a whole step to A major (D / A / E / F#m)',
    },
    sections: [
      { label: 'Intro · spoken with reverence over organ swell', chords: '(soft organ chord)', type: 'speak', lines: [
        '"First Corinthians, chapter three, verse seven…"',
        '"\'So neither the one who plants nor the one who waters is anything…\'"',
        '"\'…but only God, who makes things grow.\'"',
        '(beat enters gently)',
        '"Glory to the Most High… we give thanks…"',
      ]},
      { label: 'Verse 1', chords: '| Gmaj7 | Bm7 | Em9 | Cmaj7 | Gmaj7 | Bm7 | Am7 | D7sus  D7 |', type: 'verse', lines: [
        'I plant the probe, but I don\'t make it sense 🌱',
        'I water the lawn, but I don\'t grant the increase',
        'I write the code, but I don\'t make the seed',
        'The Almighty alone provides what we need',
        '',
        'Paul said it plain, two thousand years on —',
        'Apollos watered, but it\'s God who carries on',
        'So when the Bermuda rise and the roots run deep,',
        'I bow my head — give thanks before I sleep 🙏',
        '',
        'Mm… every blade speaks…',
        'Mm… every drop teaches…',
        'Only God makes it grow…',
      ]},
      { label: 'Pre-Chorus', chords: '| Cmaj7 | Bm7 | Am7 | D7 |', type: 'pre', lines: [
        'Hands in the dirt, but the dirt belongs to Him',
        'Probes in the ground, but the ground belongs to Him',
        'Data on the screen, but the truth belongs to Him —',
        'Lord, all I do is point to where the glory been…',
      ]},
      { label: 'Chorus 🙏🌱', chords: '| C | G | D | Em |  | C | G | Am7 | D7 |  | C | G | D | Em  Em/D |  | C | D | G | G  D7 |', type: 'hook', lines: [
        '🎵 He makes it grow!',
        '(He makes it grow!) — every leaf, every blade, every row',
        '🎵 He makes it grow!',
        '(He makes it grow!) — what we sow, only He can know',
        '',
        'I plant — (He gives) — I water — (He gives)',
        'I measure and I trust — but only God gives the increase! ✨',
        '🎵 He makes it grow…',
        '🎵 He makes it grow… (praise the Lord!)',
      ]},
      { label: 'Verse 2', chords: '(same as Verse 1)', type: 'verse', lines: [
        'See, every probe in the ground is a prayer in disguise 📡',
        'Every soak, every skip is a hand to the skies',
        'The forecast can fail and the data can lie —',
        'But the Maker of the soil? — He never deny',
        '',
        'So I built me a tool with a humble heart 🛠️',
        'Not to take the credit, just to play my part',
        'Plant and water — that\'s the work of man',
        'But the increase? — that\'s the Almighty\'s plan',
        '',
        'Mm… trust the Provider…',
        'Mm… He\'s the only Decider…',
        'Only God makes it grow…',
      ]},
      { label: 'Pre-Chorus (reprise)', chords: '| Cmaj7 | Bm7 | Am7 | D7 |', type: 'pre', lines: [
        'Round Rock sun rise, Round Rock sun fall',
        'Mercies new every morning — He hears every call 🌅',
        'Bermuda go dormant, Bermuda come \'round —',
        'Lord, You been faithful — every season I\'m found…',
      ]},
      { label: 'Bridge — the scripture, sung directly 🎺', chords: '| Cmaj7 | Gmaj7 | D | Em |  (×4, half-time)', type: 'bridge', lines: [
        '(half-time groove, choir steps back, lead vocal solo)',
        '',
        '🎵 "So neither the one who plants… nor the one who waters…"',
        '🎺 (trumpet response, mournful, beautiful)',
        '🎵 "…is anything, mm-mm-mm…"',
        '🎺 (trumpet response)',
        '',
        '🎵 "…but only God…"',
        '(choir whispers: "but only God…")',
        '🎵 "…who makes things grow…"',
        '(choir whispers: "who makes things grow…")',
        '',
        'First Corinthians, three… seven…',
        'Old as the soil, true as the rain…',
        'I plant, you water — but the Almighty…',
        'He get the glory — every time, again… 🙏',
      ]},
      { label: 'Final Chorus — UP A WHOLE STEP into A major', chords: '| D | A | E | F#m |  …(transposed)', type: 'hook', lines: [
        '🎵 HE MAKES IT GROW!',
        '(HE MAKES IT GROW!) — every leaf, every blade, every row!',
        '🎵 HE MAKES IT GROW!',
        '(HE MAKES IT GROW!) — what we sow, only HE can know!',
        '',
        'I plant — (He gives!) — I water — (He gives!)',
        'I measure and I trust — but only God gives the increase! ✨',
        '🎵 HE MAKES IT GROW…',
        '🎵 HE MAKES IT GROW… (glory! glory!)',
      ]},
      { label: 'Outro — organ + choir tag, fade', chords: '(organ sustain, choir hums fade)', type: 'speak', lines: [
        '(spoken, soft, fade)',
        '"All glory… to the Most High…"',
        '"Without the Lord… there is no TurfPulse…"',
        '"Without the Lord… there is no harvest…"',
        '"He makes it grow… He makes it grow… He makes it grow…"',
        '"…amen." 🙏',
      ]},
    ],
  },

  // ──────────────────────────────────────────────────────────────────
  {
    id: 'probe-driven',
    number: 2,
    title: 'Probe-Driven',
    subtitle: 'A Tribe Called Lawn-Care · Boom-Bap Agronomy',
    genre: '90s East-Coast Hip-Hop',
    emoji: '🎤',
    accent: 'violet',
    rollout: 'Wed 5/21 · mid-week surprise drop',
    caption: 'Wait, there\'s a third song? 🎤 Boom-bap track about probe-driven irrigation. I have no business doing this and yet here we are. TurfPulse audio drop #3. 92 BPM, F#m, dusty Rhodes loop. Volume up.',
    specs: [
      ['Tempo', '92 BPM (head-nod sweet spot)'],
      ['Key', 'F# minor (jazzy, dusty)'],
      ['Time', '4/4, swung 16ths, drums slightly behind the beat'],
      ['Vibe', 'A Tribe Called Quest "Electric Relaxation" × J Dilla Donuts × Pharrell production'],
      ['Length', '~3:00 (hip-hop economy — no fat)'],
    ],
    production: [
      ['Sample', 'Dusty Rhodes/Wurli loop in F#m, vinyl crackle baked in'],
      ['Drums', 'Chopped break — fat snare on 2&4, kick on 1 and the "and" of 3'],
      ['Bass', 'Upright bass sample, walking under the changes (Ron Carter vibe)'],
      ['Horns', 'Single muted trumpet — sparse stabs, jazz solo on the bridge'],
      ['Scratch', 'DJ scratches between verses (key word call-outs: "probe!" "fire!")'],
      ['Perc', 'Crispy hi-hats on 16ths, ride bell on the chorus, tambourine ghost'],
      ['Atmosphere', 'Vinyl crackle throughout, occasional rain/sprinkler SFX in stereo'],
      ['Vocals', 'Lead: relaxed mid-tempo flow, 70% conversational 30% rhymed. Hook: half-sung lazy melodic. Ad-libs: gang vocal "yeah!" "what!" on punchlines.'],
    ],
    arrangement: '[Intro :12] crate-dig sample swell + DJ cut → [Verse 1 :32] 16 bars → [Hook :16] half-sung melody → [Verse 2 :32] → [Hook :16] → [Bridge :16] trumpet solo, half-time → [Verse 3 :32] storyteller verse → [Hook x2 :32] last hook doubled → [Outro :08] needle scratch off',
    chords: {
      verse: '| F#m9 | Bm11 | C#m7♭5 | F#m9  E7 |   (entire song rides this 4-bar loop)',
      bridge: '| Amaj7 | G#m7 | F#m9 | B13 |  | Dmaj7 | C#m7  F#7 | Bm9 | F#m9 |',
    },
    sections: [
      { label: 'Intro — DJ scratch + sample swells', chords: '(beat builds)', type: 'speak', lines: [
        '(scratch: "probe!… probe!… probe-driven!")',
        '(sample warble fades in)',
        '"Yeah… aiyyo… check it…"',
        '"This that TurfPulse. That probe-driven. Round Rock represent."',
        '"Soil don\'t lie, baby. Let\'s get it."',
      ]},
      { label: 'Verse 1', chords: '| F#m9 | Bm11 | C#m7♭5 | F#m9  E7 |  (loop)', type: 'verse', lines: [
        'Yo — pop the cap on a cold one, sit on the porch and observe 🍻',
        'Yard\'s lookin\' lush \'cause my system got nerve',
        'Three probes deep, ain\'t no guessin\' on the curve',
        'Soil say go — that\'s the only word I serve',
        '',
        'CH1 in the dry spot, CH2 on the mean',
        'CH3 in the wet — and the data come in clean',
        'Hub on the wall like a SETI antenna 📡',
        'Pickin\' up signals from a buried cable, hella',
        '',
        'ET zero point five, that\'s a thirsty Wednesday',
        'Open-Meteo on deck — yeah, no need for guesswork, G',
        'Smith-Kerns model keepin\' the dollar spot off me',
        'Twelve percent floor and the TAMU got me coffee\'d ☕',
        '',
        'Bermuda fortified — that\'s the Texas A&M',
        'Round Rock dirt and the AgriLife stamp',
        'Y\'all out here with hose, bag, and a prayer —',
        'I got math. I got probes. And I got air. 🎤',
      ]},
      { label: 'Hook (×4)', chords: '(loop continues, lazy melodic delivery)', type: 'hook', lines: [
        '🎵 Probe-driven… probe-driven… baby, let the dirt do the talkin\'',
        'Probe-driven… probe-driven… I ain\'t sprayin\', I\'m just walkin\'',
        '(yeah!) Soil say go, Rachio fire — (what!)',
        '(yeah!) Wet spot say no, then we hold for the hour — (uh!)',
        '🎵 Probe-driven… probe-driven… yeah…',
      ]},
      { label: 'Verse 2', chords: '(loop continues)', type: 'verse', lines: [
        'Two-eighty a month on a sprinkler that\'s blind?',
        'Sippin\' on margins while you losin\' your grind 💸',
        'I cut it in half with a sensor in the line',
        'Pocket the difference, pour another, say grace, I\'m fine',
        '',
        'Forecast 24, rain inch six-tenths',
        'Skip rule active — that\'s the intelligence',
        'Wind above ten? — drift game compromised',
        'Dewpoint stuck at 65? — disease pressure rise 🦠',
        '',
        'Got the WH52 comin\' in the post on the 19th',
        'Soil temp, EC, moisture — triple threat division',
        'Plant that probe in a planter, run the new mathematics',
        'Per-zone trigger fire — that\'s the new graphics 📊',
        '',
        'Inner Circle in the cut, crest on the chest 🛡️',
        'First ten in the door — yeah, doors closin\' next',
        'No bots, no fluff, no SaaS-pitch test',
        'Just a regular guy with a probe in his vest',
      ]},
      { label: 'Bridge — half-time, muted trumpet 🎺', chords: '| Amaj7 | G#m7 | F#m9 | B13 |  …', type: 'bridge', lines: [
        '(scratch: "TurfPulse!… TurfPulse!… TurfPulse-Pulse-Pulse!")',
        '',
        'Mmm… yeah…',
        'Real ones know — (real ones know)',
        'The dirt don\'t lie — (dirt don\'t lie)',
        'The math don\'t fold — (math don\'t fold)',
        'The probe don\'t die — (probe don\'t die)',
        '',
        '🎺 (trumpet solo, 8 bars, jazzy)',
        '',
        '(scratch: "probe!… probe!… probe-driven!")',
      ]},
      { label: 'Verse 3 — the storyteller (slowed delivery)', chords: '(loop continues)', type: 'verse', lines: [
        'Started in a yard that was bumpy and brown 🪨',
        'Tripped on a dip, almost broke my crown',
        'Called Georgetown Lawn Leveling, they came right down —',
        'Smoothed it like butter, taught me how the soil sound',
        '',
        'First probe in the ground was a Friday in June',
        'Watched the moisture climb on a ten-inch tune 📈',
        'Said to my wife — "Baby, I think this is it"',
        'She said — "Cool. Don\'t let the lawn get lit." 🔥',
        '',
        'Now I\'m runnin\' the brand off a buried receiver',
        'Built the whole stack as a one-man believer',
        'Y\'all want the kit? — I left the link in the bio',
        'Build-in-public, baby — that\'s the TurfPulse motto 🎤',
        '',
        'So when you see me on a porch with the data on tap —',
        'Just a lawn nerd, forever fixin\' the gap',
        'No guess. No spray. No flood. No fluff.',
        'Just probe-driven baby — and that\'s enough.',
      ]},
      { label: 'Hook ×2 (final, doubled, gang vox)', chords: '(loop)', type: 'hook', lines: [
        '🎵 Probe-driven… probe-driven… baby, let the dirt do the talkin\'',
        'Probe-driven… probe-driven… I ain\'t sprayin\', I\'m just walkin\'',
        '(yeah!) Soil say go, Rachio fire — (what!)',
        '(yeah!) Wet spot say no, then we hold for the hour — (uh!)',
        '',
        'Probe-driven… probe-driven… (yeah, yeah, yeah)',
        'Probe-driven… probe-driven… (TurfPulse, baby…)',
      ]},
      { label: 'Outro — needle scratch off', chords: '(silence + vinyl crackle)', type: 'speak', lines: [
        '"Yeah… that\'s that probe-driven…"',
        '"Round Rock. TurfPulse. Inner Circle."',
        '"…peace." ✌️',
      ]},
    ],
  },

  // ──────────────────────────────────────────────────────────────────
  {
    id: 'soak-it-up',
    number: 3,
    title: 'Soak It Up',
    subtitle: 'A funk anthem for probe-driven irrigation',
    genre: 'Funk Swagger',
    emoji: '🎺',
    accent: 'rose',
    rollout: 'Sun 5/24 7:00 PM CST',
    caption: 'I built a smart lawn-care app. Then I wrote a funk song about it because that\'s apparently who I am now. 🎺 Full lyrics + chords at getturfpulse.com/about/the-songbook',
    specs: [
      ['Tempo', '112 BPM'],
      ['Key', 'D minor (modulates up to E minor on the final chorus)'],
      ['Time', '4/4, swung 16ths on the hat'],
      ['Vibe', 'Bruno Mars × Mark Ronson — strut, swagger, finger-snap groove'],
      ['Length', '~3:20'],
    ],
    production: [
      ['Kit', 'Linn LM-1 snare (gated, hard) · TR-808 kick · live tambourine on 2 & 4'],
      ['Bass', 'Slap bass — root + octave + ghost notes, locked to kick'],
      ['Keys', 'Clavinet (verse) · Rhodes pad (pre-chorus) · synth brass stabs (chorus)'],
      ['Horns', 'Trumpet + tenor sax + trombone — tight 3-part hits on the "1"'],
      ['Perc', 'Cowbell every 4 bars · congas under the bridge'],
      ['Vox', 'Lead in chest voice (D3-A4) · falsetto answer in the hook · gang "HEY!" backings'],
    ],
    arrangement: '[Intro :08] horn fanfare + 4-bar drum break → [Verse 1 :32] clavinet + bass only, drums in at bar 5 → [Pre :16] build with Rhodes pad + tambourine → [Chorus :32] full band, gang vocals, horn stabs → [Verse 2 :32] → [Pre :16] → [Chorus :32] → [Bridge :24] half-time, wah guitar solo, congas → [Final Chorus x2 :48] key lift up to E minor → [Outro :12] horn tag + cowbell fade',
    chords: {
      verse: '| Dm9 | Dm9 | Gm7 | A7♯9 |  | Dm9 | Dm9 | Bb13  A7 | Dm9 |',
      preChorus: '| Bbmaj7 | C9 | Dm7 | Em7♭5  A7 |',
      bridge: '| Fmaj9 | Em7 | Dm9 | Gm7  C9 |   (half-time, wah)',
      modulation: 'Final chorus: up a whole step to E minor (Em9 / Am7 / B7♯9)',
    },
    sections: [
      { label: 'Intro — spoken, swaggered', chords: '(horn stab, drum break)', type: 'speak', lines: [
        '"Hah! Listen — your lawn been lyin\' to you, baby."',
        '"But the dirt? The dirt don\'t lie."',
        '"TurfPulse… let\'s go."',
      ]},
      { label: 'Verse 1', chords: '| Dm9 | Dm9 | Gm7 | A7♯9 |  | Dm9 | Dm9 | Bb13  A7 | Dm9 |', type: 'verse', lines: [
        'Sun comin\' up, you grabbin\' that hose 🌅',
        'Spray a little here, spray wherever it goes',
        'Neighbor on the left got a yellow patch',
        'Neighbor on the right got a water bill — catch!',
        '',
        'You been guessin\', you been stressin\', readin\' the grass',
        'Three bucks a gallon and it\'s leakin\' through fast',
        'But I got a little secret, lemme pull you aside —',
        'Three little probes and the truth\'s inside.',
      ]},
      { label: 'Pre-Chorus (build)', chords: '| Bbmaj7 | C9 | Dm7 | Em7♭5  A7 |', type: 'pre', lines: [
        'One in the dry, one in the mean,',
        'One in the wet — uh! — keep that data clean',
        'Hub on the wall got a Rachio kiss',
        'Soil say "go," and we don\'t miss…',
      ]},
      { label: 'Chorus 🎺', chords: '| Dm9 | Dm9 | Gm7 | A7♯9 |  | Dm9 | Dm9 | Bb13  A7 | Dm9 |', type: 'hook', lines: [
        'Soak it up! (soak it up!)',
        'Let the dirt do the talkin\', baby —',
        'Soak it up! (soak it up!)',
        'TurfPulse got me feelin\' immortal, lately!',
        'No more guess, no more spray, no more flood —',
        'Just the data, just the green, just the good!',
        '(Hey!) Hey, hey — soak it up!',
        '(Hey!) Hey, hey — TurfPulse, what!',
      ]},
      { label: 'Verse 2', chords: '(same groove)', type: 'verse', lines: [
        'Two-eighty a month on a sprinkler that\'s blind?',
        'Sippin\' on margins while you losin\' your mind',
        'Round Rock rebate? I filed mine.',
        'Got a hundred fifty back and a smile on time',
        '',
        'Bermuda lookin\' lush, dollar spot? — declined ✋',
        'Smith-Kerns model keepin\' fungus offline',
        'Twelve percent floor and the TAMU stamp —',
        'Texas heat ain\'t breakin\' this camp.',
      ]},
      { label: 'Pre-Chorus (reprise)', chords: '| Bbmaj7 | C9 | Dm7 | Em7♭5  A7 |', type: 'pre', lines: [
        'CH1, CH2, CH3 (yeah!)',
        'Buried in the ground like a dynasty',
        'Dewpoint stuck and the wind is right —',
        'Spray window open, baby — let\'s ignite!',
      ]},
      { label: 'Bridge 🎸 (half-time, wah)', chords: '| Fmaj9 | Em7 | Dm9 | Gm7  C9 |', type: 'bridge', lines: [
        'Mmm — let me tell ya somethin\'…',
        'Rachio\'s the bridge — (yeah, yeah)',
        'But TurfPulse the brain — (c\'mon, c\'mon)',
        'You don\'t need a green thumb,',
        'Just a probe in the rain — (woo!)',
        '',
        'Inner Circle in the cut, crest on the chest 🛡️',
        'Fifty bucks a year and we built the rest',
        'No bots, no fluff, no salesman pitch —',
        'Just a regular guy with a regular itch.',
      ]},
      { label: 'Final Chorus — UP A STEP into Em9', chords: '| Em9 | Em9 | Am7 | B7♯9 |   (key lift)', type: 'hook', lines: [
        'SOAK IT UP! (soak it up!)',
        'Let the dirt do the talkin\', baby —',
        'SOAK IT UP! (soak it up!)',
        'TURFPULSE GOT ME FEELIN\' IMMORTAL, LATELY!',
        '(Hey!) No more brown — (Hey!) no more drown —',
        '(Hey!) Just the greenest yard in the whole damn town!',
        'Soak it up — TurfPulse, WHAT!',
      ]},
      { label: 'Outro — cowbell + horns fade', chords: '(fade)', type: 'speak', lines: [
        '"That\'s the sound of a lawn that knows what it\'s doin\', baby…"',
        '"…TurfPulse. We don\'t guess. We measure."',
      ]},
    ],
  },

  // ──────────────────────────────────────────────────────────────────
  {
    id: 'smooth-like-that',
    number: 4,
    title: 'Smooth Like That',
    subtitle: 'A Georgetown Lawn Leveling Tribute',
    genre: 'Silk Sonic Soul',
    emoji: '🎷',
    accent: 'emerald',
    rollout: 'Sun 5/24 7:30 PM CST · 30 min after Soak It Up',
    caption: '@Georgetown Lawn Leveling — y\'all started this whole thing. I made you a song. 🎷 Couldn\'t help myself. All love.',
    specs: [
      ['Tempo', '96 BPM (groove pocket — less hype, more soul)'],
      ['Key', 'B♭ major (classic Motown warmth)'],
      ['Time', '4/4, behind-the-beat drums, swung feel'],
      ['Vibe', 'Silk Sonic "Leave the Door Open" × Stevie Wonder "Sir Duke" × Anderson .Paak gratitude'],
      ['Length', '~3:45'],
    ],
    production: [
      ['Kit', 'Vintage Ludwig kit · brushed snare on 2&4 · gentle ride bell'],
      ['Bass', 'Fingerstyle Fender P-bass — round, woody, walks under the changes'],
      ['Guitar', 'Clean Strat (Verse: chicken-pickin\' double-stops · Chorus: open chord rakes)'],
      ['Keys', 'Wurlitzer EP (intro/verse) · Hammond B3 swell (chorus pad)'],
      ['Horns', 'Big band — 2 trumpets, alto+tenor sax, trombone (chorus shouts)'],
      ['Strings', 'Cinematic legato pad on the bridge only (lift moment)'],
      ['Perc', 'Shaker on every 8th, congas on the bridge, finger snaps'],
      ['Vox', 'Lead: smooth chest voice (B♭3-F4), tons of falsetto melisma. Backing: 3-part harmony stack ("ooh-aaah") choir-style.'],
    ],
    arrangement: '[Intro :12] Wurlitzer alone, "Mm... Georgetown..." → [Verse 1 :40] sparse, bass + drums + Wurli → [Pre :16] band leans in, snare crack → [Chorus :32] full horns, harmonies, "smooth like that" → [Verse 2 :40] → [Pre :16] → [Chorus :32] → [Bridge :32] half-time, strings up, personal-story moment → [Final Chorus :40] modulate up to B major + horn fanfare → [Outro :17] call-and-response with horns, fade',
    chords: {
      verse: '| Bbmaj9 | Gm11 | Cm9 | F13 |  | Bbmaj9 | Gm11 | Cm9  F7 | Bbmaj9 |',
      preChorus: '| Ebmaj7 | Dm7 | Cm7 | F7sus  F7 |',
      chorus: '| Bbmaj7 | Gm9 | Eb6 | F7 |  | Bbmaj7 | Gm9 | Cm7 | F7  F7♯9 |',
      bridge: '| Gm9 | Cm9 | Fmaj7 | Bbmaj7 |  | Ebmaj7 | Dm7 | Cm7  F7sus | Bbmaj9 |',
      modulation: 'Final chorus: up a half-step to B major (Bmaj7 / G#m9 / E6 / F#7)',
    },
    sections: [
      { label: 'Intro — Wurlitzer alone, smoky', chords: '(soft horn swell, drums fade in)', type: 'speak', lines: [
        '"Mm-hm…"',
        '"Y\'all know what time it is, baby…"',
        '"This one\'s for the folks who started it all…"',
        '"Georgetown… Lawn Leveling…"',
      ]},
      { label: 'Verse 1 (behind the beat)', chords: '| Bbmaj9 | Gm11 | Cm9 | F13 |  | Bbmaj9 | Gm11 | Cm9  F7 | Bbmaj9 |', type: 'verse', lines: [
        'Lawn was a war zone, baby — bumps everywhere 🪨',
        'Mower was bouncin\', I\'d trip on the air',
        'Five-foot dip by the maple tree,',
        'And a hump by the gate where the sprinkler used to be 🤕',
        '',
        'Yeah, I tried to fix it — bag of sand, a rake, and a prayer',
        'Made it worse, somehow, like I added more there',
        'Wife said, "Baby… call somebody" —',
        'That\'s when I picked up the phone and dialed somebody…',
      ]},
      { label: 'Pre-Chorus (snare leans in)', chords: '| Ebmaj7 | Dm7 | Cm7 | F7sus  F7 |', type: 'pre', lines: [
        'Truck pulled up, polite knock at the door 🚪',
        'Boots on the grass like they done this before',
        'Pulled out a screed, pulled out the load —',
        'Then the magic started flowin\'…',
      ]},
      { label: 'Chorus 🎷🎷🎷', chords: '| Bbmaj7 | Gm9 | Eb6 | F7 |  | Bbmaj7 | Gm9 | Cm7 | F7  F7♯9 |', type: 'hook', lines: [
        'They call it Georgetown Lawn Leveling —',
        'Sand poured slow like a love song singin\'',
        'Smooth like that (smooth like that) ✨',
        'Smooth like that (smooth like that)',
        '',
        'Took my disaster, made a runway out of grass —',
        'Said "give it two weeks, you\'ll forget you had a past"',
        'Smooth like that (smooth like that) 🌱',
        'Smooth like that (smooth like that)',
        '',
        'Mm — Georgetown done it again, baby…',
      ]},
      { label: 'Verse 2', chords: '(same)', type: 'verse', lines: [
        'Two weeks later, I\'m walkin\' barefoot 🦶',
        'No more rolled ankles where the bird bath used to put \'em',
        'Mower glidin\' like a Cadillac on cream —',
        'I sat on the porch and I cried at the seam',
        '',
        'But the real gift wasn\'t the level — uh-uh —',
        'It was the conversation while they worked the shovel 🗣️',
        '"How deep your roots? What grass you grow?',
        'Why you waterin\' when the soil don\'t show?"',
      ]},
      { label: 'Pre-Chorus (reprise)', chords: '| Ebmaj7 | Dm7 | Cm7 | F7sus  F7 |', type: 'pre', lines: [
        'Lightbulb hit me on a Tuesday in June —',
        'Started measurin\', started readin\', started buyin\' too soon',
        'One probe, two probe, three in the ground —',
        'Now I\'m runnin\' a whole damn company off that sound 🛠️',
      ]},
      { label: 'Bridge 🎻 (half-time, strings rise)', chords: '| Gm9 | Cm9 | Fmaj7 | Bbmaj7 |  | Ebmaj7 | Dm7 | Cm7  F7sus | Bbmaj9 |', type: 'bridge', lines: [
        'Mmm…',
        '',
        'See, sometimes the best teachers',
        'Don\'t call themselves that — (no they don\'t)',
        'They just hand you the shovel',
        'And say "baby, start where the bumps at" — (c\'mon)',
        '',
        'They didn\'t sell me an app,',
        'Didn\'t pitch me no plan,',
        'Just leveled my yard',
        'And explained who I am — (woo!)',
        '',
        'Now I\'m buildin\' a tool 🛠️',
        'So the next guy don\'t guess —',
        'But I\'d be nothing without',
        'The folks in the GTX 💚',
      ]},
      { label: 'Final Chorus — UP A HALF STEP into B major', chords: '| Bmaj7 | G#m9 | E6 | F#7 |  …(transposed)', type: 'hook', lines: [
        'They call it GEORGETOWN LAWN LEVELING — (say it again now!)',
        'Sand poured slow like a love song singin\'!',
        'SMOOTH LIKE THAT! (smooth like that!) ✨',
        'SMOOTH LIKE THAT! (smooth like that!)',
        '',
        'Took my disaster — made a runway out of grass!',
        'Said "two weeks, baby, forget you had a past!"',
        'SMOOTH LIKE THAT! (smooth like that!) 🌱',
        'SMOOTH LIKE THAT! (smooth like that!)',
        '',
        'Mm — Georgetown done it again, baby…',
      ]},
      { label: 'Outro — call & response, fade', chords: '(big band tag, ride cymbal fade)', type: 'speak', lines: [
        '"Who taught me how?" 🎷 (Georgetown!)',
        '"Who poured the sand?" (Georgetown!)',
        '"Who leveled my heart?" (Georgetown!)',
        '"…thank you, fam."',
      ]},
    ],
  },

  // ──────────────────────────────────────────────────────────────────
  {
    id: 'one-drop-at-a-time',
    number: 5,
    title: 'One Drop at a Time',
    subtitle: 'A Roots Reggae Meditation',
    genre: 'Roots Reggae',
    emoji: '🌴',
    accent: 'sky',
    rollout: 'Mon 5/25 7:00 PM CST · Memorial Day evening',
    caption: 'After 17 days of the glow up — sit back. Let the system run. 🌴 Final track of the TurfPulse Songbook drops on Memorial Day. One drop at a time, baby.',
    specs: [
      ['Tempo', '76 BPM (classic "Night Nurse" / "Don\'t Turn Around" pocket)'],
      ['Key', 'A minor (warm, meditative)'],
      ['Time', '4/4, drums BEHIND the beat — let it breathe'],
      ['Vibe', 'Gregory Isaacs lovers rock × Aswad UK reggae soul × Morgan Heritage roots × Barrington Levy toasting (bridge)'],
      ['Length', '~4:00'],
    ],
    production: [
      ['Kit', 'ONE-DROP — kick on beat 3, rim-shot on 3, hi-hat 8ths. NO snare on 2 or 4. The space IS the groove.'],
      ['Bass', 'Walking melodic bass — Robbie Shakespeare territory. Sits between kick and rim, never on the 1.'],
      ['Guitar 1', '"Skank" — clean, palm-muted offbeats (chk-chk on 2 & 4)'],
      ['Guitar 2', 'Aswad-style melodic licks between vocal lines (clean, reverb)'],
      ['Organ', '"The Bubble" — Hammond B3 doubling the offbeats with skank. Aswad signature.'],
      ['Piano', 'Sparse Wurli stabs on the "and" of beats'],
      ['Horns', '3-part horn line ONLY on chorus + outro tag (melodic, not stabs)'],
      ['Perc', 'Shaker every 8th, light bongos on the bridge'],
      ['Vox', 'LEAD: smooth baritone (A3-D4), Gregory Isaacs lovers-rock register. HARMONY: 3-part stack on chorus (Morgan Heritage). TOAST: Barrington Levy hype DJ flow on bridge.'],
    ],
    arrangement: '[Intro :16] bubble organ + skank guitar build, "easy now…" → [Verse 1 :48] smooth lovers-rock → [Pre :16] lift, harmonies enter → [Chorus :32] horns + 3-part vocals → [Verse 2 :48] → [Pre :16] → [Chorus :32] → [Bridge :40] Barrington Levy toasting over half-time → [Final Chorus :40] all-in, modulate up to B minor → [Outro :32] horns tag + fade',
    chords: {
      verse: '| Am9 | Dm9 | Am9 | E7sus  E7 |  | Am9 | Dm9 | Fmaj7 | E7 |',
      preChorus: '| Fmaj7 | Em7 | Dm7 | E7 |',
      chorus: '| C | G | Am | Fmaj7 |  | C | G | Dm7 | E7  E7♯9 |',
      bridge: '| Am | G | Fmaj7 | E7 |  (×4, half-time)',
      modulation: 'Final chorus: up a whole step to B minor (D / A / Bm / Gmaj7)',
    },
    sections: [
      { label: 'Intro — spoken, smooth', chords: '(bubble organ swells)', type: 'speak', lines: [
        '"Easy now…"',
        '"Cool runnings, baby…"',
        '"This one\'s a slow rider…"',
        '"TurfPulse, mon… one drop at a time…"',
      ]},
      { label: 'Verse 1', chords: '| Am9 | Dm9 | Am9 | E7sus  E7 |  | Am9 | Dm9 | Fmaj7 | E7 |', type: 'verse', lines: [
        'Easy now, easy now — water inna mi pocket 💧',
        'Don\'t go runnin\' to the spigot just because mi want fi lock it',
        'Probe inna di soil say "wait, nuh, mon, wait"',
        'Roots dem a grow when yuh sleep — don\'t rush di gate',
        '',
        'Texas sun a beat down, but di Bermuda strong 💪',
        'Patience long, mi friend — patience long',
        'Sprinkler want fi run, but di data say "no, sah"',
        'Forty-six percent moisture? — that\'s a healthy soul, sah',
        '',
        'Mm… let it ride…',
        'Let the dirt decide…',
        'One drop at a time…',
      ]},
      { label: 'Pre-Chorus', chords: '| Fmaj7 | Em7 | Dm7 | E7 |', type: 'pre', lines: [
        'Watch the chart, watch the line —',
        'Three percent a day, that\'s the natural decline',
        'Don\'t fight the slope, don\'t break the sign —',
        'Trust the dirt, baby, trust the dirt this time…',
      ]},
      { label: 'Chorus 🎺🌴 (3-part harmony, horns)', chords: '| C | G | Am | Fmaj7 |  | C | G | Dm7 | E7  E7♯9 |', type: 'hook', lines: [
        '🎵 One drop at a time',
        '(one drop at a time) — let the soil sing the line',
        '🎵 One drop at a time',
        '(one drop at a time) — every Bermuda gonna shine',
        '',
        'Roots a-grow deep, baby — roots a-grow deep 🌱',
        'Faith in the data when you fall asleep',
        '🎵 One drop at a time — that\'s the rhythm, that\'s the rhyme…',
        '🎵 One drop… at a time…',
      ]},
      { label: 'Verse 2', chords: '(same as Verse 1)', type: 'verse', lines: [
        'Round Rock to Round Hill, mi salute every yard 🇯🇲🤝🇺🇸',
        'If yuh waterin\' on a guess, mi friend, life a hard',
        'Forty dollar probe, plus a hub on the wall —',
        'Now di soil call di shots, not di forecast at all',
        '',
        'See di neighbor \'pon a sprinkler — three times a week',
        'Lawn lookin\' yellow \'cause di roots dem weak',
        'Mi tell \'im "come check it", mi show \'im di tile —',
        'Now him on di Inner Circle, smilin\' for a while 🛡️',
        '',
        'Mm… spread di word…',
        'Let di dirt be heard…',
        'One drop at a time…',
      ]},
      { label: 'Pre-Chorus', chords: '| Fmaj7 | Em7 | Dm7 | E7 |', type: 'pre', lines: [
        'WH-five-two comin\' on the nineteenth tide ⚡',
        'Soil temp, EC, all three on the ride',
        'Plant \'im inna di planter, watch di numbers align —',
        'Trust the dirt, baby, trust the dirt this time…',
      ]},
      { label: 'Bridge — Barrington Levy toasting 🎤', chords: '| Am | G | Fmaj7 | E7 |  (×4, half-time)', type: 'bridge', lines: [
        'Yo! Shi-da-shi-da-shi-dap! 🎤',
        'Step inna di yard, mi a tell yuh \'bout di setup, wha-gwan!',
        'Skiddly-bop! — three probe inna di groun\', listenin\' on…',
        'CH1, CH2, CH3 — every channel a sing \'long!',
        '',
        'Hear me now!',
        'Five AM mi a leave for di work, can\'t mow inna di mornin\'!',
        '(uh!) — but di system don\'t sleep, di system keep performin\'!',
        '(yeah!) — sprinkler fire when di soil say so —',
        '(rrrap!) — TurfPulse, baby, that\'s the only way mi know!',
        '',
        'Bo! Bo! — pull up di selecta!',
        'Drop it inna Round Rock, drop it inna di sector!',
        '🎤 Eee-yo! — Inner Circle in di buildin\'!',
        'Crest \'pon mi chest — first ten, wha gwaan with it!',
        '',
        'Whoa-oh-oh…',
        '(easin\' down, returning to smooth…)',
        'One drop at a time, baby… 🌴',
      ]},
      { label: 'Final Chorus — UP A WHOLE STEP into Bm', chords: '| D | A | Bm | Gmaj7 |  …(transposed)', type: 'hook', lines: [
        '🎵 ONE DROP AT A TIME!',
        '(one drop at a time!) — let the soil sing the line!',
        '🎵 ONE DROP AT A TIME!',
        '(one drop at a time!) — every Bermuda gonna shine!',
        '',
        'Roots a-grow deep, baby — roots a-grow deep! 🌱',
        'Faith in the data when you fall asleep!',
        '🎵 ONE DROP AT A TIME — that\'s the rhythm, that\'s the rhyme…',
        '🎵 ONE DROP… AT A TIME…',
      ]},
      { label: 'Outro — horns tag + toaster ad-libs, fade', chords: '(bubble organ + horns fade)', type: 'speak', lines: [
        '(toaster, fading)',
        '"TurfPulse, baby… cool runnings…"',
        '"Round Rock to Round Hill, mi salute…"',
        '"One love. One drop. One yard at a time…"',
        '"…peace and blessings, mon." 🌴',
      ]},
    ],
  },
  // ──────────────────────────────────────────────────────────────────
  // 6th song — TEASER / UNRELEASED. Marked `unreleased: true` so the
  // /about/the-songbook page can render it behind a "coming soon" veil
  // instead of the full lyric / chord chart treatment.
  {
    id: '100-and-holdin',
    number: 6,
    title: "100° and Holdin'",
    subtitle: 'Texas Outlaw Country · the home-turf anthem for the dog-days dry-down',
    genre: 'Texas Outlaw Country',
    emoji: '🤠',
    accent: 'amber',
    unreleased: true,
    rollout: 'Drops with the first July heat advisory · Round Rock summer 2026',
    caption: 'Round Rock summer hits 100° before lunch and your neighbor is out there with a hose looking spooked. But you? You\'ve got probes in the ground, you trust the math, you let the bermuda do what it was built to do. This one\'s for the homeowners who learned the hard way that panic-watering is how lawns die — not heat. 🤠 Texas country, slow-walkin\' bassline, pedal steel, and a chorus you can sing in the truck.',
    specs: [
      ['Tempo', '92 BPM (slow-walkin\', dust-on-the-boots country swagger)'],
      ['Key', 'D major (open, prairie, room for the pedal steel)'],
      ['Time', '4/4, country shuffle on the verses, half-time feel on the bridge'],
      ['Vibe', 'Sturgill Simpson × Cody Jinks × early Waylon × a Round Rock screen-porch in August'],
      ['Length', '~3:50'],
    ],
    production: [
      ['Kit', 'Brushes on the verses, sticks-on-rim country shuffle on the chorus, half-time tom rolls into the bridge'],
      ['Bass', 'Upright-feel electric, root-fifth walking — slow, confident, never rushed'],
      ['Guitar', 'Telecaster on the verse (clean, twangy), acoustic strummed under the chorus, pedal steel ALWAYS in the air'],
      ['Pedal Steel', 'Lonesome cries in the verse pockets, soaring bends on the chorus tag'],
      ['Fiddle', 'Optional — single-line counter-melody on verse 2 only'],
      ['Organ', 'Soft Hammond pad under the chorus (Texas country B3, not church)'],
      ['Vox', 'LEAD: weathered baritone (D3-A4), like he\'s telling you something important on the porch. HARMONIES: high-tenor third on the chorus tag, "100° and holdin\'..."'],
    ],
    arrangement: '[Intro :16] pedal steel cry + acoustic strum → [Verse 1 :32] sparse, tele clean → [Chorus :32] band kicks in, harmonies on tag → [Verse 2 :32] fuller, fiddle counter-line → [Chorus :32] → [Bridge :40] half-time, single-tom heartbeat, the "TAMU said twelve" reveal → [Final Chorus :40] full band, pedal steel on top, dynamic lift → [Outro :20] just acoustic + pedal steel + voice, fade on "holdin\'…"',
    chords: {
      verse: '| D | A | G | D |  | D | A | G | D  A |',
      chorus: '| G | D | Em | A |  | G | D | A | D |',
      bridge: '| Bm | A | G | D |  (×4, half-time)',
      modulation: 'No key change — final chorus lifts dynamically, not harmonically (this is country, not Broadway)',
    },
    sections: [
      { label: 'Intro · pedal steel cry, acoustic strum', chords: '| D | A | G | D |', type: 'instrumental', lines: [
        '(pedal steel weeps, slow)',
        '(porch creaks. Cicadas. Boots on wood.)',
      ]},
      { label: 'Verse 1', chords: '| D | A | G | D |  | D | A | G | D  A |', type: 'verse', lines: [
        'Forecast said a hundred again by ten ☀️',
        'My neighbor\'s draggin\' hoses around the yard, again',
        'He told me, "Friend, your bermuda gonna brown by noon"',
        'I just sipped my coffee and watched the dewpoint balloon',
      ]},
      { label: 'Chorus', chords: '| G | D | Em | A |  | G | D | A | D |', type: 'hook', lines: [
        '🎵 A hundred degrees and holdin\' —',
        'the bermuda\'s holdin\' too 🌾',
        'Probe says eighteen, that\'s still above the floor —',
        'a hundred degrees and holdin\', honey, I learned to trust the truth',
        'Deeper roots, baby — that\'s what the probe is for',
      ]},
      { label: 'Verse 2', chords: '| D | A | G | D |  | D | A | G | D  A |', type: 'verse', lines: [
        'Used to panic-water at the first sign of heat 🚿',
        'Wakin\' up at three AM just to feel the lawn\'s heartbeat',
        'But the smart-split schedule and the skip-gate stack —',
        'they taught me how to leave the hose alone and never look back',
      ]},
      { label: 'Chorus', chords: '| G | D | Em | A |  | G | D | A | D |', type: 'hook', lines: [
        '🎵 A hundred degrees and holdin\' —',
        'the bermuda\'s holdin\' too 🌾',
        'Probe says eighteen, that\'s still above the floor —',
        'a hundred degrees and holdin\', honey, I learned to trust the truth',
        'Deeper roots, baby — that\'s what the probe is for',
      ]},
      { label: 'Bridge — half-time, single-tom heartbeat', chords: '| Bm | A | G | D |  (×4)', type: 'bridge', lines: [
        '(slow tom… one… two… three… four…)',
        '',
        'The Aggies down at A&M ran the numbers in the field 🎓',
        'Wherley said the wilt-stress floor on bermuda is twelve',
        'I run thirty on the Ecowitt — that\'s my comfort line',
        'So when the soil starts whisperin\', I\'ve still got time',
        '',
        'Round Rock heat don\'t scare a man who\'s read the math 📐',
        'Smith-Kerns watchin\' my dewpoint on the daily path',
        'FAO-fifty-six tellin\' me how much water she\'ll need —',
        'and I trust the receipts more than I trust the breed',
      ]},
      { label: 'Final Chorus — band lifts, pedal steel soars', chords: '| G | D | Em | A |  | G | D | A | D |', type: 'hook', lines: [
        '🎵 A hundred degrees and HOLDIN\' —',
        'the bermuda\'s holdin\' too 🌾',
        'Probe says eighteen, that\'s still above the floor —',
        'a hundred degrees and HOLDIN\', honey, I LEARNED to trust the truth',
        'Deeper roots, baby — that\'s what the probe is for',
        '',
        '(pedal steel crests)',
        'Hundred degrees… and holdin\'…',
      ]},
      { label: 'Outro — fade on acoustic + pedal steel + voice', chords: '| D | A | G | D |  (fade)', type: 'speak', lines: [
        '(spoken, half-smile)',
        '"Out here in Round Rock, Texas… the lawn don\'t need a hero."',
        '"It needs a probe… and a man who can read it."',
        '',
        '(fade on pedal steel sigh)',
        '🤠',
      ]},
    ],
  },
];

export const SONGBOOK_META = {
  title: 'TurfPulse Songbook · Vol. 1',
  subtitle: 'Five songs · Five genres · One probe-driven worldview',
  scripture: '"So neither the one who plants nor the one who waters is anything, but only God, who makes things grow." — 1 Corinthians 3:7',
  byline: 'Written for TurfPulse by a regular guy who probes his own dirt · All glory to the Most High',
};
